import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { colors, styles } from "../../utils/theme";
import { device } from "../../utils/breakpoints";
import ReactMarkdown from "react-markdown";
import DarkBackground from "../elements/DarkBackground";
import HangingCard from "../elements/HangingCard";
import BannerTitle from "../core/BannerTitle";
import { BackgroundElement } from "../../styles/SharedStyles";

const StepsBanner = ({ data, fullWidth = false }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const DATA = data[lang];

  return (
    <Wrapper style={{ marginInline: fullWidth ? 0 : 19 }}>
      <DarkBackground
        fullWidth={fullWidth}
        style={{ borderRadius: fullWidth ? 0 : 6 }}
      >
        <BrownSpiceBgElement
          src={"/assets/img/styling/spice-splash-brown.webp"}
          alt={""}
        />
        <HerbsAndSpicesBgElementOne
          src={"/assets/img/styling/herbs-and-spices-4.webp"}
          alt={""}
        />
        <HerbsAndSpicesBgElementTwo
          src={"/assets/img/styling/herbs-and-spices-4.webp"}
          alt={""}
        />
        <BannerContainer fullWidth={fullWidth}>
          <BannerTitle dark>{DATA.title}</BannerTitle>
          <CardsContainer fullWidth={fullWidth}>
            {DATA.steps.map((item, index) => (
              <HangingCard dark key={index}>
                <InnerCardContainer fullWidth={fullWidth}>
                  <h3>{`${index + 1}. ${item.title}`}</h3>
                  <img src={item.icon} alt={""} />
                  <ReactMarkdown>{item.description}</ReactMarkdown>
                </InnerCardContainer>
              </HangingCard>
            ))}
          </CardsContainer>
        </BannerContainer>
      </DarkBackground>
    </Wrapper>
  );
};

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4rem;

  & > article {
    &:nth-child(even) {
      transform: rotate(-1.5deg);
    }
    &:nth-child(odd) {
      transform: rotate(0.5deg);
    }
  }

  @media only screen and ${device.lg} {
    justify-content: space-between;
    flex-direction: row;
    gap: 3rem;
  }

  @media ${device.xl} {
    gap: ${({ fullWidth }) => (fullWidth ? "6rem" : "4rem")};
  }
`;

const InnerCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  padding: 1.25rem;
  position: relative;
  height: 100%;

  h3 {
    font-family: "Poppins", sans-serif;
    color: ${colors.primaryDark};
    font-size: 22px;
    font-weight: 600;
    padding-right: 2rem;
    margin-bottom: 1rem;
  }

  img {
    width: 40px;
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
  }

  @media ${device.lg} {
    padding: ${({ fullWidth }) => (fullWidth ? "1.5rem" : "1rem")};

    h3 {
      font-size: ${({ fullWidth }) => (fullWidth ? "22px" : "21px")};
      padding-right: ${({ fullWidth }) => (fullWidth ? "3rem" : "42px")};
    }

    img {
      width: ${({ fullWidth }) => (fullWidth ? "42px" : "40px")};
      top: ${({ fullWidth }) => (fullWidth ? "1rem" : "0.75rem")};
      right: ${({ fullWidth }) => (fullWidth ? "1rem" : "0.75rem")};
    }
  }
`;

const BannerContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  gap: 3rem;
  min-height: 480px;
  padding: 6rem 2rem;
  margin-inline: auto;
  max-width: 1400px;

  @media ${device.md} {
    padding: 6rem 4rem;
  }

  @media ${device.lg} {
    padding: ${({ fullWidth }) => (fullWidth ? "4rem 2rem" : "4rem")};
  }
`;

const Wrapper = styled.section`
  margin-block: ${styles.banner_padding_mobile};

  @media ${device.lg} {
    margin-block: ${styles.banner_padding_desktop};
  }
`;

const BrownSpiceBgElement = styled(BackgroundElement)`
  opacity: 0.6;
  transform: rotate(0deg) scaleY(-1);
  z-index: -2;

  bottom: -480px;
  left: -300px;

  @media ${device.lg} {
    left: -200px;
  }

  @media ${device.xl} {
    left: -100px;
  }
`;

const HerbsAndSpicesBgElementOne = styled(BackgroundElement)`
  transform: rotate(-130deg);
  width: 240px;
  top: 60%;
  left: -150px;

  @media ${device.md} {
    top: 30px;
    left: -130px;
  }

  @media ${device.lg} {
    width: 280px;
    left: -150px;
  }

  @media ${device.xl} {
    width: 320px;
    left: -190px;
  }
`;

const HerbsAndSpicesBgElementTwo = styled(BackgroundElement)`
  transform: rotate(30deg);
  width: 240px;
  top: -230px;
  right: -70px;

  @media ${device.md} {
    right: -50px;
  }

  @media ${device.lg} {
    width: 280px;
    top: -250px;
    right: -70px;
  }

  @media ${device.xl} {
    width: 320px;
    top: -280px;
    right: -90px;
  }
`;

export default StepsBanner;
